import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL, VAPID_PUBLIC_KEY } from '../config';
import '../styles/Notifications.css';

interface Notification {
  id: string;
  message: string;
  timestamp: string;
  read: boolean;
  type: string;
  senderAvatar?: string;
}

const NotificationButton: React.FC = () => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);
  const [subscriptionJson, setSubscriptionJson] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const standalone = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
    setIsIOS(ios);
    setIsStandalone(standalone);
  }, []);

  const checkSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
        if (sub) setSubscriptionJson(JSON.stringify(sub, null, 2));
        return sub;
      } catch (e) {
        console.error('Error checking subscription:', e);
      }
    }
    return null;
  };

  const handleDeepReset = async () => {
    if (!window.confirm("This will forcefully restart the notification system. Continue?")) return;
    setResetting(true);
    try {
      // 1. Unregister all service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (let reg of registrations) {
        await reg.unregister();
        console.log("Unregistered SW:", reg);
      }

      // 2. Clear all caches
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }

      // 3. Re-register fresh
      await navigator.serviceWorker.register('/service-worker.js');
      
      // 4. Try to re-subscribe
      setTimeout(() => {
        subscribeToPush();
        setResetting(false);
      }, 1000);

    } catch (e) {
      console.error("Reset failed:", e);
      setResetting(false);
      alert("Reset failed. Please refresh your browser manually.");
    }
  };

  useEffect(() => {
    checkSubscription().then(sub => {
      // Auto-prompt if not subscribed but logged in
      if (!sub && user && token) {
        // On iOS, this only triggers if in standalone mode
        if (!isIOS || isStandalone) {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              subscribeToPush();
            }
          });
        }
      }
    });
  }, [user, token, isIOS, isStandalone]);

  const subscribeToPush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      
      // Try to clear existing
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      // Convert base64 VAPID key to Uint8Array for the browser
      const padding = '='.repeat((4 - VAPID_PUBLIC_KEY.length % 4) % 4);
      const base64 = (VAPID_PUBLIC_KEY + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: outputArray
      });

      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(sub)
      });

      setIsSubscribed(true);
      setSubscriptionJson(JSON.stringify(sub, null, 2));
      alert('🔔 Phone notifications enabled!');
    } catch (err) {
      console.error('Failed to subscribe:', err);
      alert('Could not enable phone notifications. Make sure you are using a modern browser and have allowed notifications.');
    }
  };

  const [activePopup, setActivePopup] = useState<Notification | null>(null);
  const lastNotifIdRef = useRef<string | null>(null);
  const isFirstLoadRef = useRef(true);

  const fetchNotifications = () => {
    if (user && token) {
      fetch(`${API_URL}/api/notifications/${user.email}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const sorted = data.reverse();
            setNotifications(sorted);
            
            // Check for new notification to show popup
            if (sorted.length > 0) {
              const newest = sorted[0];
              // Only show popup if it's a NEW notification ID we haven't seen before
              // and NOT on the very first load of the component
              if (!isFirstLoadRef.current && lastNotifIdRef.current && newest.id !== lastNotifIdRef.current) {
                // Also ensure it's actually newer than what we had (prevent popup when deleting)
                const isTrulyNewer = !notifications.some(existing => existing.id === newest.id);
                if (isTrulyNewer) {
                  setActivePopup(newest);
                  setTimeout(() => setActivePopup(null), 5000);
                  
                  // Trigger native Chrome notification if permitted
                  if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('The F.I.N.D. Update', {
                      body: newest.message,
                      icon: '/logo192.png'
                    });
                  }
                }
              }
              lastNotifIdRef.current = newest.id;
            }
            isFirstLoadRef.current = false;
          } else {
            setNotifications([]);
          }
        })
        .catch(err => console.error('Error fetching notifications:', err));
    }
  };

  const deleteNotification = (id: string) => {
    if (token) {
      // Optimistic update: remove from UI immediately
      setNotifications(prev => prev.filter(n => n.id !== id));
      
      fetch(`${API_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(() => {
          // Refresh from server to ensure sync, but the UI is already clean
          setTimeout(fetchNotifications, 1000); 
        })
        .catch(err => {
          console.error('Error deleting notification:', err);
          fetchNotifications(); // Revert on error
        });
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, 60000); // Check every 60 seconds when tab is active
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="notification-wrapper">
      {/* REAL-TIME POPUP TOAST */}
      {activePopup && (
        <div className={`notification-popup-toast ${activePopup.type.toLowerCase()}`} onClick={() => { setShowDropdown(true); setActivePopup(null); }}>
          <div className="popup-icon">
            {activePopup.senderAvatar ? <img src={activePopup.senderAvatar} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%' }} /> : '🔔'}
          </div>
          <div className="popup-content">
            <strong>New Update!</strong>
            <p>{activePopup.message}</p>
          </div>
          <button className="popup-close" onClick={(e) => { e.stopPropagation(); setActivePopup(null); }}>×</button>
        </div>
      )}

      <button className="notification-bell" onClick={() => setShowDropdown(!showDropdown)}>
        🔔 {notifications?.length > 0 && <span className="notification-badge">{notifications.length}</span>}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="dropdown-header">
            <h3>Notifications</h3>
            <button className="close-dropdown" onClick={() => setShowDropdown(false)}>×</button>
          </div>

          {/* PUSH NOTIFICATION PROMPT */}
          {!isSubscribed && (
            <div style={{ padding: '15px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
              {isIOS && !isStandalone ? (
                <div style={{ background: '#fff9e6', padding: '12px', borderRadius: '12px', border: '1px solid #f9d342', fontSize: '0.85rem' }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>📲 Enable iPhone Alerts</p>
                  <p style={{ margin: '0 0 10px 0', color: '#666' }}>To get notifications on iPhone, you must add this site to your home screen:</p>
                  <ol style={{ textAlign: 'left', margin: '0', paddingLeft: '20px', color: '#333' }}>
                    <li>Tap the <strong>Share</strong> button (box with arrow)</li>
                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                    <li>Open the app from your home screen</li>
                  </ol>
                </div>
              ) : 'serviceWorker' in navigator && 'PushManager' in window ? (
                <button 
                  onClick={subscribeToPush}
                  style={{ 
                    background: 'var(--primary-color)', 
                    color: 'white', 
                    border: 'none', 
                    padding: '10px 16px', 
                    borderRadius: '20px', 
                    fontSize: '0.85rem', 
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  🔔 Turn on Phone Notifications
                </button>
              ) : (
                <p style={{ fontSize: '0.8rem', color: '#999' }}>Your browser doesn't support phone notifications.</p>
              )}
            </div>
          )}
          {isSubscribed && (
            <div style={{ padding: '15px', borderBottom: '1px solid #eee', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button 
                onClick={() => {
                  fetch(`${API_URL}/api/push/test`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                  }).catch(err => console.error('Test Push Error:', err));
                }}
                style={{ 
                  background: '#2ecc71', 
                  color: 'white', 
                  border: 'none', 
                  padding: '10px 16px', 
                  borderRadius: '20px', 
                  fontSize: '0.85rem', 
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                🚀 Send Test Push
              </button>
              
              <button 
                onClick={() => setShowTroubleshoot(!showTroubleshoot)}
                style={{ background: 'none', border: 'none', color: '#999', fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {showTroubleshoot ? 'Hide Troubleshoot' : 'Still not working?'}
              </button>

              {showTroubleshoot && (
                <div style={{ textAlign: 'left', background: '#f8f9fa', padding: '10px', borderRadius: '10px', marginTop: '5px' }}>
                  <p style={{ fontSize: '0.75rem', margin: '0 0 5px 0', fontWeight: 'bold' }}>Diagnostics:</p>
                  <ul style={{ fontSize: '0.7rem', margin: 0, paddingLeft: '15px', color: '#666' }}>
                    <li>Subscribed: {isSubscribed ? 'Yes' : 'No'}</li>
                    <li>Permission: {Notification.permission}</li>
                    <li>Standalone: {isStandalone ? 'Yes' : 'No'}</li>
                    <li>iOS: {isIOS ? 'Yes' : 'No'}</li>
                  </ul>
                  <button 
                    onClick={handleDeepReset}
                    disabled={resetting}
                    style={{ marginTop: '10px', width: '100%', padding: '8px', fontSize: '0.7rem', borderRadius: '5px', border: '1px solid #ffcccc', background: '#fff0f0', color: '#e74c3c', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    {resetting ? '🔄 Resetting...' : '🧨 Deep Reset System'}
                  </button>
                  <button 
                    onClick={subscribeToPush}
                    style={{ marginTop: '10px', width: '100%', padding: '5px', fontSize: '0.7rem', borderRadius: '5px', border: '1px solid #ddd', background: 'white' }}
                  >
                    🔄 Refresh Connection
                  </button>
                  {subscriptionJson && (
                    <div style={{ marginTop: '10px' }}>
                      <p style={{ fontSize: '0.6rem', color: '#999', wordBreak: 'break-all', overflow: 'hidden', height: '40px' }}>
                        ID: {JSON.parse(subscriptionJson).endpoint.substring(0, 50)}...
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {notifications?.length === 0 ? (
            <p className="no-notifs">No new updates.</p>
          ) : (
            <div className="notif-list">
              {notifications?.map(n => (
                <div key={n.id} className={`notif-item ${n.type.toLowerCase()}`} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {n.senderAvatar && <img src={n.senderAvatar} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid white', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', flexShrink: 0 }} />}
                  <div className="notif-content" style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 5px 0' }}>{n.message}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <small style={{ color: '#999' }}>{new Date(n.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                      <button 
                        className="clear-notif-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(n.id);
                        }}
                        style={{ padding: '2px 8px', fontSize: '0.7rem' }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationButton;
